//* Libraries imports
import { useState, useCallback , useEffect, useRef } from "react";
import { Card, Alert, Button } from "react-bootstrap";
import { Document, Page, pdfjs } from 'react-pdf';
import "./pdf-viewer.scss";

//* Components imports
import Icon from "../../../../components/common/Icon";

//* Icons imports
import { 
  FaDownload, 
  FaSearchPlus, 
  FaSearchMinus, 
  FaUndo, 
  FaChevronLeft, 
  FaChevronRight,
  FaSync,
  FaHome
} from "react-icons/fa";

// Configure PDF.js worker for react-pdf (recommended approach)
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface PdfViewerProps {
  pdfUrl: string;
  fileName?: string;
  hasCriteriosZero?: boolean;
  disableDownload?: boolean;
  downloadPermissions?: {
    canDownload: boolean;
    showButton: boolean;
    requireAuth?: boolean;
  };
}

export function PdfViewer({ 
  pdfUrl,
  fileName = "document",
  hasCriteriosZero = false, 
  disableDownload = false,
  downloadPermissions
}: PdfViewerProps) {  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numPages, setNumPages] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [pdfWidth, setPdfWidth] = useState<number>(800);
  const [scale, setScale] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  
  // Drag functionality states
  const [translateX, setTranslateX] = useState<number>(0);
  const [translateY, setTranslateY] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  // Responsive PDF width based on screen size
  useEffect(() => {
    const updatePdfWidth = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 576) {
        setPdfWidth(Math.min(screenWidth - 40, 300));
      } else if (screenWidth < 768) {
        setPdfWidth(Math.min(screenWidth - 60, 500));
      } else if (screenWidth < 992) {
        setPdfWidth(Math.min(screenWidth - 100, 700));
      } else {
        setPdfWidth(800);
      }
    };

    updatePdfWidth();
    window.addEventListener('resize', updatePdfWidth);
    return () => window.removeEventListener('resize', updatePdfWidth);
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setError(null);
    setNumPages(numPages);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error("PDF load error:", error);
    setError("Erro ao carregar o PDF");
  }, []);
  // PDF Control functions
  const goToPrevPage = () => setPageNumber(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(numPages, prev + 1));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 3));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));
  const resetZoom = () => setScale(1);
  const rotateClockwise = () => setRotation(prev => (prev + 90) % 360);
  const rotateCounterclockwise = () => setRotation(prev => (prev - 90 + 360) % 360);

  // Drag functionality
  const resetPosition = () => {
    setTranslateX(0);
    setTranslateY(0);
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - translateX, y: e.clientY - translateY });
  }, [translateX, translateY]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !dragStart) return;
    
    const newTranslateX = e.clientX - dragStart.x;
    const newTranslateY = e.clientY - dragStart.y;
    
    setTranslateX(newTranslateX);
    setTranslateY(newTranslateY);
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  // Mouse event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleDownload = useCallback(async () => {
    // Check permissions (legacy support)
    if (disableDownload) return;
    
    // Check new permissions system
    if (downloadPermissions && !downloadPermissions.canDownload) return;
    
    try {
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
    }
  }, [fileName, pdfUrl, disableDownload, downloadPermissions]);

  if (!pdfUrl) {
    return (
      <Card className="pdf-viewer-card border-0 shadow-sm">
        <Card.Body className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
          <Alert variant="warning" className="text-center">
            <Alert.Heading className="h5">PDF não disponível</Alert.Heading>
            <p className="mb-0">O arquivo PDF não está disponível no momento.</p>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={`pdf-viewer-card border-0 shadow-sm position-relative ${hasCriteriosZero ? 'has-criterios-zero' : ''} ${isDragging ? 'pdf-dragging' : ''}`}>
      <Card.Body className="p-0">
        {/* PDF Controls at top */}
        <div className="pdf-controls-top d-flex justify-content-between align-items-center p-3 border-bottom">
          {/* PDF Navigation Controls */}
          <div className="d-flex align-items-center gap-2">            <Button
              variant="outline-secondary"
              size="sm"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              className="d-flex align-items-center"
              title="Página anterior"
            >
              <Icon icon={FaChevronLeft} />
            </Button>
            
            <span className="page-info px-2 text-muted small">
              {pageNumber} / {numPages || 1}
            </span>
              <Button
              variant="outline-secondary"
              size="sm"
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              className="d-flex align-items-center"
              title="Próxima página"
            >
              <Icon icon={FaChevronRight} />
            </Button>
          </div>

          {/* Zoom and Rotation Controls */}
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"              onClick={zoomOut}
              disabled={scale <= 0.5}
              className="d-flex align-items-center"
              title="Diminuir zoom"
            >
              <Icon icon={FaSearchMinus} />
            </Button>
            
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={resetZoom}
              className="d-flex align-items-center px-2"
              title="Resetar zoom"
            >
              {Math.round(scale * 100)}%
            </Button>
            
            <Button
              variant="outline-secondary"
              size="sm"              onClick={zoomIn}
              disabled={scale >= 3}
              className="d-flex align-items-center"
              title="Aumentar zoom"
            >
              <Icon icon={FaSearchPlus} />
            </Button>
            
            <div className="vr mx-2"></div>
            
            <Button
              variant="outline-secondary"
              size="sm"              onClick={rotateCounterclockwise}
              className="d-flex align-items-center"
              title="Girar anti-horário"
            >
              <Icon icon={FaUndo} />
            </Button>
              <Button
              variant="outline-secondary"
              size="sm"
              onClick={rotateClockwise}
              className="d-flex align-items-center"
              title="Girar horário"
            >
              <Icon icon={FaSync} />
            </Button>
            
            <div className="vr mx-2"></div>
            
            <Button
              variant="outline-secondary"
              size="sm"              onClick={resetPosition}
              className="d-flex align-items-center"
              title="Resetar posição"
            >
              <Icon icon={FaHome} />
            </Button>
          </div>

          {/* Download Button */}
          {(!disableDownload || (downloadPermissions?.showButton && downloadPermissions?.canDownload)) && (
            <Button
              variant="outline-success"
              size="sm"
              onClick={handleDownload}              className="d-flex align-items-center gap-2"
              disabled={disableDownload || (downloadPermissions && !downloadPermissions.canDownload)}
            >
              <Icon icon={FaDownload} />
              Download PDF
            </Button>
          )}
        </div>

        {error && (
          <div className="pdf-error-overlay d-flex align-items-center justify-content-center position-absolute w-100 h-100">
            <Alert variant="danger" className="text-center m-3">
              <Alert.Heading>Erro ao carregar PDF</Alert.Heading>
              <p className="mb-0">{error}</p>
            </Alert>
          </div>
        )}        <div className="pdf-canvas-container">
          <div 
            className="pdf-viewer-wrapper"
            ref={pdfContainerRef}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <div
              onMouseDown={handleMouseDown}
              style={{
                transform: `translate(${translateX}px, ${translateY}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease',
                cursor: isDragging ? 'grabbing' : 'grab'
              }}
            >
              <Document 
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="text-muted">Carregando PDF...</p>
                  </div>
                }
              >
                <Page 
                  pageNumber={pageNumber}
                  width={pdfWidth * scale}
                  rotate={rotation}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
